import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import s from "../modules/TrainerCreate.module.css";
import { useT, useI18nReady } from "../components/TextProvider";

/**
 * Страница: Редактирование письменного вопроса для контрольной работы
 * API: GET/PUT https://apitr.subxonqulov.uz/api/kurs-yozma-savollar/{id}/
 *   Поля: kurs (id), savol_uz/ru/en (HTML), javob_uz/ru/en (HTML), tartib (order)
 */

const YOZMA_SAVOLLAR_ENDPOINT =
  process.env.REACT_APP_YOZMA_SAVOLLAR_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/kurs-yozma-savollar/";

const COURSES_ENDPOINT =
  process.env.REACT_APP_FANLAR_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/fanlar/";

const USER_ROLES_ENDPOINT =
  process.env.REACT_APP_USER_ROLES_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/user-roles/";

// ===== helpers: auth
function getStoredUser() {
  const ls = window.localStorage.getItem("auth:user");
  const ss = window.sessionStorage.getItem("auth:user");
  const raw = ls ?? ss;
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function getAnyTokenFromStorage() {
  const pick = (s) =>
    s.getItem("auth:access") || s.getItem("auth:token") || s.getItem("auth:key") ||
    s.getItem("auth:jwt") || s.getItem("auth:auth_token") || null;
  return pick(window.localStorage) || pick(window.sessionStorage);
}

function makeAuthHeader(tokenLike) {
  if (!tokenLike) return {};
  const token = String(tokenLike);
  const forcedScheme = process.env.REACT_APP_AUTH_SCHEME;
  if (forcedScheme) return { Authorization: `${forcedScheme} ${token}` };
  const looksLikeJwt = token.startsWith("eyJ");
  return looksLikeJwt ? { Authorization: `Bearer ${token}` } : { Authorization: `Token ${token}` };
}

// Helper to check if user is admin
async function checkIsAdmin(userId, token) {
  try {
    const res = await fetch(USER_ROLES_ENDPOINT, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token ? makeAuthHeader(token) : {}),
      },
      credentials: "include",
    });
    if (!res.ok) return false;
    const list = await res.json().catch(() => []);
    const isAdmin = Array.isArray(list)
      ? list.some(r => Number(r?.user) === Number(userId) && Number(r?.role) === 3)
      : false;
    return isAdmin;
  } catch {
    return false;
  }
}

export default function EditWrittenQuestion() {
  const t = useT();
  const ready = useI18nReady();
  const navigate = useNavigate();
  const { id } = useParams();

  const [form, setForm] = useState({
    kurs: "",
    tartib: "",
  });

  const [savolUzHTML, setSavolUzHTML] = useState("");
  const [savolRuHTML, setSavolRuHTML] = useState("");
  const [savolEnHTML, setSavolEnHTML] = useState("");
  const [jvobUzHTML, setJvobUzHTML] = useState("");
  const [jvobRuHTML, setJvobRuHTML] = useState("");
  const [jvobEnHTML, setJvobEnHTML] = useState("");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);

  const user = useMemo(() => getStoredUser(), []);

  // CKEditor refs & ids
  const editorIdUz = useMemo(() => "savol-uz-editor-" + Math.random().toString(36).slice(2), []);
  const editorIdRu = useMemo(() => "savol-ru-editor-" + Math.random().toString(36).slice(2), []);
  const editorIdEn = useMemo(() => "savol-en-editor-" + Math.random().toString(36).slice(2), []);
  const editorIdJvobUz = useMemo(() => "jvob-uz-editor-" + Math.random().toString(36).slice(2), []);
  const editorIdJvobRu = useMemo(() => "jvob-ru-editor-" + Math.random().toString(36).slice(2), []);
  const editorIdJvobEn = useMemo(() => "jvob-en-editor-" + Math.random().toString(36).slice(2), []);

  // CKEditor instances ref
  const instancesRef = useRef({});

  // Load question data
  useEffect(() => {
    let abort = false;
    (async () => {
      if (!id || !user?.id) return;
      try {
        const token = getAnyTokenFromStorage();
        const isAdmin = await checkIsAdmin(user.id, token);
        const res = await fetch(`${YOZMA_SAVOLLAR_ENDPOINT}${id}/`, {
          method: "GET",
          headers: { "Content-Type": "application/json", ...(token ? makeAuthHeader(token) : {}) },
          credentials: "include",
        });
        
        if (!res.ok) {
          throw new Error("Savol topilmadi");
        }
        
        const data = await res.json();
        
        // Check if user owns this question or is admin
        const dataUserId = Number(data?.user) || Number(data?.kurs?.user);
        if (!isAdmin && dataUserId !== Number(user.id)) {
          throw new Error("Sizda bu savol ni tahrirlash ruxsati yo'q");
        }
        
        if (!abort) {
          setForm({
            kurs: String(data.kurs || ""),
            tartib: String(data.tartib || ""),
          });
          setSavolUzHTML(data.savol_uz || "");
          setSavolRuHTML(data.savol_ru || "");
          setSavolEnHTML(data.savol_en || "");
          setJvobUzHTML(data.javob_uz || "");
          setJvobRuHTML(data.javob_ru || "");
          setJvobEnHTML(data.javob_en || "");
          setDataLoaded(true);
        }
      } catch (e) {
        if (!abort) setError(e?.message || "Savol yuklashda xatolik yuz berdi");
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [id, user?.id]);

  // Load courses list
  useEffect(() => {
    let abort = false;
    (async () => {
      if (!user?.id) return;
      try {
        const token = getAnyTokenFromStorage();
        const isAdmin = await checkIsAdmin(user.id, token);
        const res = await fetch(COURSES_ENDPOINT, {
          method: "GET",
          headers: { "Content-Type": "application/json", ...(token ? makeAuthHeader(token) : {}) },
          credentials: "include",
        });
        const data = await res.json().catch(() => []);
        let arr = Array.isArray(data) ? data : [];
        
        // Администраторы видят все курсы, обычные пользователи - только свои
        if (!isAdmin) {
          arr = arr.filter((course) => Number(course?.user) === Number(user.id));
        }
        
        if (!abort) setCourses(arr);
      } catch {
        if (!abort) setCourses([]);
      }
    })();
    return () => { abort = true; };
  }, [user?.id]);

  function onChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function validateForm() {
    if (!form.kurs) return "Kursni tanlang";
    if (!savolUzHTML.trim()) return "Savolni o'zbekcha kiriting";
    if (!savolRuHTML.trim()) return "Savolni ruscha kiriting";
    if (!savolEnHTML.trim()) return "Savolni inglizcha kiriting";
    if (!jvobUzHTML.trim()) return "Javobni o'zbekcha kiriting";
    if (!jvobRuHTML.trim()) return "Javobni ruscha kiriting";
    if (!jvobEnHTML.trim()) return "Javobni inglizcha kiriting";
    if (!user?.id) return "Foydalanuvchi topilmadi. Qaytadan tizimga kiring.";
    return "";
  }

  // === CKEditor 4 via CDN ===
  function ensureCKEScript() {
    return new Promise((resolve, reject) => {
      if (window.CKEDITOR) return resolve();
      const script = document.createElement("script");
      script.src = "https://cdn.ckeditor.com/4.22.1/full-all/ckeditor.js";
      script.onload = () => resolve();
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  const commonCkeConfig = {
    language: "ru",
    height: 200,
    contentsLangDirection: "ltr",
    contentsCss: [
      "https://cdn.ckeditor.com/4.22.1/full-all/contents.css",
      "body { direction: ltr; text-align: left; }",
    ],
    toolbar: [
      { name: "document", items: ["Source", "-", "Preview"] },
      { name: "clipboard", items: ["Cut", "Copy", "Paste", "PasteText", "PasteFromWord", "-", "Undo", "Redo"] },
      { name: "editing", items: ["Find", "Replace", "SelectAll"] },
      "/",
      { name: "basicstyles", items: ["Bold", "Italic", "Underline", "Strike", "Subscript", "Superscript", "-", "RemoveFormat"] },
      { name: "paragraph", items: ["NumberedList", "BulletedList", "-", "Outdent", "Indent", "-", "Blockquote", "-", "JustifyLeft", "JustifyCenter", "JustifyRight", "JustifyBlock"] },
      { name: "links", items: ["Link", "Unlink", "Anchor"] },
      { name: "insert", items: ["Image", "Table", "HorizontalRule", "SpecialChar"] },
      "/",
      { name: "styles", items: ["Styles", "Format", "Font", "FontSize"] },
      { name: "colors", items: ["TextColor", "BGColor"] },
      { name: "tools", items: ["Maximize"] },
    ],
    extraPlugins: "colorbutton,font,justify",
    removePlugins: "elementspath,notification",
    resize_enabled: true,
  };

  // Helper to init a single editor
  const initEditor = (editorId, initialData, setter) => {
    const elem = document.getElementById(editorId);
    if (!elem) return;
    if (window.CKEDITOR.instances[editorId]) {
      window.CKEDITOR.instances[editorId].destroy(true);
    }
    const inst = window.CKEDITOR.replace(editorId, commonCkeConfig);
    if (!inst) return;
    
    inst.on("instanceReady", () => {
      inst.setData(initialData || "");
      try { inst.resize('100%', 200); } catch {}
    });
    
    inst.on("change", () => {
      setter(inst.getData());
    });
    
    instancesRef.current[editorId] = inst;
  };

  // Initialize all editors after data is loaded
  useEffect(() => {
    if (!dataLoaded) return;
    let destroyed = false;
    ensureCKEScript().then(() => {
      if (destroyed) return;
      initEditor(editorIdUz, savolUzHTML, setSavolUzHTML);
      initEditor(editorIdRu, savolRuHTML, setSavolRuHTML);
      initEditor(editorIdEn, savolEnHTML, setSavolEnHTML);
      initEditor(editorIdJvobUz, jvobUzHTML, setJvobUzHTML);
      initEditor(editorIdJvobRu, jvobRuHTML, setJvobRuHTML);
      initEditor(editorIdJvobEn, jvobEnHTML, setJvobEnHTML);
    });
    
    return () => {
      destroyed = true;
      // Clean up on unmount
      Object.values(instancesRef.current).forEach(inst => {
        if (inst && window.CKEDITOR.instances[inst.name]) {
          inst.destroy(true);
        }
      });
      instancesRef.current = {};
    };
  }, [dataLoaded]);

  // Submit handler
  async function handleUpdate(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    const v = validateForm();
    if (v) {
      setError(v);
      return;
    }

    try {
      setSubmitting(true);
      const token = getAnyTokenFromStorage();

      const payload = {
        kurs: String(form.kurs),
        user: String(user.id),
        savol_uz: savolUzHTML.trim(),
        savol_ru: savolRuHTML.trim(),
        savol_en: savolEnHTML.trim(),
        javob_uz: jvobUzHTML.trim(),
        javob_ru: jvobRuHTML.trim(),
        javob_en: jvobEnHTML.trim(),
        tartib: form.tartib ? String(form.tartib) : "1",
      };

      console.log("Payload being sent:", payload);

      const res = await fetch(`${YOZMA_SAVOLLAR_ENDPOINT}${id}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? makeAuthHeader(token) : {}),
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      const data = await res.json().catch(() => ({}));
      console.log("Response status:", res.status);
      console.log("Response data:", data);

      if (!res.ok) {
        console.error("Server error response:", {
          status: res.status,
          data: data,
        });

        let errorMsg = "Savolni tahrirlashda xatolik";
        
        if (typeof data === "string") {
          errorMsg = data;
        } else if (data?.detail) {
          errorMsg = data.detail;
        } else if (data?.error) {
          errorMsg = data.error;
        } else if (data?.message) {
          errorMsg = data.message;
        } else if (data && typeof data === "object") {
          const firstError = Object.entries(data).find(([key, value]) => value);
          if (firstError) {
            const [key, value] = firstError;
            if (Array.isArray(value)) {
              errorMsg = `${key}: ${value.join(", ")}`;
            } else {
              errorMsg = `${key}: ${value}`;
            }
          }
        }
        
        throw new Error(errorMsg);
      }

      setSuccess("Savol muvaffaqiyatli tahrirlandi!");
      setTimeout(() => navigate("/MyQuestions", { replace: true }), 1000);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Error during submit:", err);
      setError(err?.message || "Xatolik yuz berdi");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className={s.wrapper}>
        <div className={s.card}>
          <div className={s.info}>Savol yuklanmoqda…</div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.wrapper}>
      <div className={s.card}>
        <h1 className={s.title}>{t('editWQTitle')}</h1>

        <form className={s.form} onSubmit={handleUpdate}>
          <div className={s.field}>
            <label className={s.label}>{t('editWQCourse')}</label>
            <select className={s.select} name="kurs" value={form.kurs} onChange={onChange} required>
              <option value="" disabled>
                {t('CWQselectCourse')}
              </option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>

          {/* SAVOLLAR SECTION */}
          <div className={s.section}>
            <h3 className={s.sectionTitle}>Savollar</h3>
            
            <div className={s.field}>
              <label className={s.label}>Savol (O'zbekcha) *</label>
              <textarea id={editorIdUz} defaultValue={savolUzHTML} />
            </div>

            <div className={s.field}>
              <label className={s.label}>Savol (Ruscha) *</label>
              <textarea id={editorIdRu} defaultValue={savolRuHTML} />
            </div>

            <div className={s.field}>
              <label className={s.label}>Savol (Inglizcha) *</label>
              <textarea id={editorIdEn} defaultValue={savolEnHTML} />
            </div>
          </div>

          {/* JAVOBLAR SECTION */}
          <div className={s.section}>
            <h3 className={s.sectionTitle}>Javoblar</h3>
            
            <div className={s.field}>
              <label className={s.label}>Javob (O'zbekcha) *</label>
              <textarea id={editorIdJvobUz} defaultValue={jvobUzHTML} />
            </div>

            <div className={s.field}>
              <label className={s.label}>Javob (Ruscha) *</label>
              <textarea id={editorIdJvobRu} defaultValue={jvobRuHTML} />
            </div>

            <div className={s.field}>
              <label className={s.label}>Javob (Inglizcha) *</label>
              <textarea id={editorIdJvobEn} defaultValue={jvobEnHTML} />
            </div>
          </div>

          <div className={s.field}>
            <label className={s.label}>Savol tartib raqami</label>
            <input
              className={s.input}
              name="tartib"
              value={form.tartib}
              onChange={onChange}
              placeholder="1, 2, 3..."
              type="number"
            />
          </div>

          {error && <div className={s.error}>{error}</div>}
          {success && <div className={s.success}>{success}</div>}

          <div className={s.actions}>
            <button className={s.submit} type="submit" disabled={submitting}>
              {submitting ? "Saqlayapmiz…" : "Saqlash"}
            </button>
            <button type="button" className={s.secondary} onClick={() => navigate(-1)} disabled={submitting}>
              Bekor qilish
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}